import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  createAuthApi,
  type AnalysisDetail,
  type AnalysisListItem,
  type AnalysisRequest,
  type AuthApi,
  type AuthSession,
  type AuthUser,
} from './auth/auth-api.js';
import { PublicReportPage, publicReportToken } from './PublicReport.js';
import { SettingsPage } from './SettingsPage.js';
import { initializeTelegramMiniApp } from './telegram/telegram.js';

type ViewState =
  | { status: 'loading' }
  | { status: 'authenticated'; session: AuthSession; loggingOut: boolean }
  | { status: 'outside-telegram' }
  | { status: 'admin-login' }
  | { status: 'signed-out' }
  | { status: 'error'; message: string };

/** Sentinel Telegram-id браузерного админа (вход по паролю) — реальные id ≥ 1. */
const ADMIN_SENTINEL_TELEGRAM_ID = '0';

function isAdminRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.replace(/\/+$/, '') === '/admin';
}

/** Страница настроек админки — тот же приём с pathname, что и у /admin. */
function isSettingsRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.replace(/\/+$/, '') === '/admin/settings';
}

/** Токен публичного пересказа из адреса; null — обычный вход в приложение. */
function shareTokenFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  return publicReportToken(window.location.pathname);
}

export type AppAuthApi = AuthApi;

export interface TelegramBridge {
  initialize(): string | null;
}

interface AppProps {
  auth?: AppAuthApi;
  telegram?: TelegramBridge;
}

const defaultTelegram: TelegramBridge = {
  initialize: () => initializeTelegramMiniApp(),
};

function displayName(user: AuthUser): string {
  return user.firstName || user.username || 'пользователь';
}

function UserAvatar({ user }: { user: AuthUser }) {
  if (user.photoUrl) {
    return <img className="profile__avatar" src={user.photoUrl} alt="" />;
  }

  return (
    <div className="profile__avatar profile__avatar--fallback" aria-hidden="true">
      {displayName(user).slice(0, 1).toUpperCase()}
    </div>
  );
}

type AnalysisFormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'queued'; analysis: AnalysisRequest }
  | { status: 'error'; message: string };

function AnalysisForm({ api, onQueued }: { api: AppAuthApi; onQueued?: () => void }) {
  const [url, setUrl] = useState('');
  const [state, setState] = useState<AnalysisFormState>({ status: 'idle' });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = url.trim();
    if (state.status === 'submitting' || trimmed.length === 0) return;

    setState({ status: 'submitting' });
    try {
      const analysis = await api.createAnalysis(trimmed);
      setState({ status: 'queued', analysis });
      setUrl('');
      onQueued?.();
    } catch (error) {
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Не удалось поставить анализ в очередь',
      });
    }
  }

  return (
    <form className="analysis-form" onSubmit={submit}>
      <label className="analysis-form__label" htmlFor="analysis-url">
        Ссылка на YouTube-видео
      </label>
      <div className="analysis-form__row">
        <input
          id="analysis-url"
          className="analysis-form__input"
          type="url"
          inputMode="url"
          placeholder="https://youtu.be/…"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          disabled={state.status === 'submitting'}
        />
        <button
          className="button"
          type="submit"
          disabled={state.status === 'submitting' || url.trim().length === 0}
        >
          {state.status === 'submitting' ? 'Отправляем…' : 'Проверить'}
        </button>
      </div>

      {state.status === 'queued' && (
        <p className="analysis-form__note analysis-form__note--ok" role="status">
          Видео принято в очередь на анализ
        </p>
      )}
      {state.status === 'error' && (
        <p className="analysis-form__note analysis-form__note--error" role="alert">
          {state.message}
        </p>
      )}
    </form>
  );
}

const STATUS_META: Record<string, { label: string; mod: string }> = {
  QUEUED: { label: 'В очереди', mod: 'wait' },
  RUNNING: { label: 'Анализ…', mod: 'run' },
  COMPLETED: { label: 'Готово', mod: 'ok' },
  FAILED: { label: 'Ошибка', mod: 'fail' },
  CANCELLED: { label: 'Отменён', mod: 'wait' },
};

const PENDING_STATUSES = new Set(['QUEUED', 'RUNNING']);

const PROVIDER_LABEL: Record<string, string> = {
  anthropic: 'Claude',
  proxy: 'Claude (прокси)',
  gigachat: 'GigaChat',
};

function analyzedBy(detail: AnalysisDetail): string | null {
  for (const step of detail.steps) {
    const usage = step.output?.usage;
    if (usage?.provider || usage?.model) {
      const label = usage.provider ? (PROVIDER_LABEL[usage.provider] ?? usage.provider) : '';
      return [label, usage.model].filter(Boolean).join(' · ');
    }
  }
  return null;
}

/** Человекочитаемое имя провайдера для бейджа в списке ('gigachat' → 'GigaChat'). */
function providerLabel(provider: string | null | undefined): string | null {
  if (!provider) return null;
  return PROVIDER_LABEL[provider] ?? provider;
}

function AnalysisDetailView({ detail }: { detail: AnalysisDetail }) {
  const by = analyzedBy(detail);
  return (
    <div className="detail">
      {by && <p className="detail__by">🤖 Разобрано: {by}</p>}
      {detail.status === 'FAILED' && detail.errorMessage && (
        <p className="analyses__note analyses__note--error">{detail.errorMessage}</p>
      )}

      {detail.summary?.overview && (
        <div className="detail__block">
          <p className="eyebrow">Краткое содержание</p>
          <p className="detail__summary">{detail.summary.overview}</p>
          {detail.summary.keyTopics && detail.summary.keyTopics.length > 0 && (
            <div className="detail__chips">
              {detail.summary.keyTopics.map((topic, index) => (
                <span key={index} className="chip">
                  {topic}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {detail.claims.length > 0 && (
        <div className="detail__block">
          <p className="eyebrow">Утверждения ({detail.claims.length})</p>
          <ul className="claims">
            {detail.claims.map((claim) => (
              <li key={claim.order} className="claim">
                <span className="claim__text">{claim.text}</span>
                <span className="claim__meta">
                  {claim.category && <span className="claim__tag">{claim.category}</span>}
                  <span className="claim__risk">
                    важность {claim.importance} · риск {claim.riskLevel}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="detail__steps">
        {detail.steps.map((step) => (
          <span key={step.stage} className={`step step--${step.status.toLowerCase()}`}>
            {step.stage}
          </span>
        ))}
      </div>
    </div>
  );
}

function AnalysisCard({
  api,
  item,
  isAdmin,
  variant = 'row',
}: {
  api: AppAuthApi;
  item: AnalysisListItem;
  isAdmin: boolean;
  variant?: 'row' | 'card';
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AnalysisDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const meta = STATUS_META[item.status] ?? { label: item.status, mod: 'wait' };
  const title = item.video.title ?? item.video.canonicalUrl;
  const requester = item.requestedBy
    ? [item.requestedBy.firstName, item.requestedBy.username ? `@${item.requestedBy.username}` : null]
        .filter(Boolean)
        .join(' ')
    : '';

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      setLoading(true);
      try {
        setDetail(await api.getAnalysis(item.id));
      } catch {
        // Ошибка загрузки детали не критична — карточка остаётся свёрнутой по смыслу.
      } finally {
        setLoading(false);
      }
    }
  }

  if (variant === 'card') {
    return (
      <li className={`analysis-card${open ? ' analysis-card--open' : ''}`}>
        <button
          className="analysis-card__head"
          type="button"
          onClick={() => void toggle()}
          aria-expanded={open}
        >
          <span className="analysis-card__thumb-wrap">
            {item.video.thumbnailUrl ? (
              <img className="analysis-card__thumb" src={item.video.thumbnailUrl} alt="" />
            ) : (
              <span className="analysis-card__thumb analysis-card__thumb--empty" aria-hidden="true">
                ▶
              </span>
            )}
            <span className={`status status--${meta.mod}`}>{meta.label}</span>
          </span>
          <span className="analysis-card__body">
            <span className="analysis-card__title">{title}</span>
            {item.video.channelName && (
              <span className="analysis-card__channel">{item.video.channelName}</span>
            )}
            {isAdmin && requester && (
              <span className="analysis-card__requester">от {requester}</span>
            )}
            {providerLabel(item.provider) && (
              <span className="analysis-card__provider">🤖 {providerLabel(item.provider)}</span>
            )}
          </span>
        </button>

        {open && (
          <div className="analysis-card__detail">
            {loading && <p className="analyses__note">Загружаем…</p>}
            {detail && <AnalysisDetailView detail={detail} />}
          </div>
        )}
      </li>
    );
  }

  return (
    <li className="analysis-item">
      <button
        className="analysis-item__head"
        type="button"
        onClick={() => void toggle()}
        aria-expanded={open}
      >
        {item.video.thumbnailUrl ? (
          <img className="analysis-item__thumb" src={item.video.thumbnailUrl} alt="" />
        ) : (
          <span className="analysis-item__thumb analysis-item__thumb--empty" aria-hidden="true">
            ▶
          </span>
        )}
        <span className="analysis-item__body">
          <span className="analysis-item__title">{title}</span>
          {item.video.channelName && (
            <span className="analysis-item__channel">{item.video.channelName}</span>
          )}
          {isAdmin && requester && <span className="analysis-item__requester">от {requester}</span>}
        </span>
        <span className={`status status--${meta.mod}`}>{meta.label}</span>
      </button>

      {open && (
        <div className="analysis-item__detail">
          {loading && <p className="analyses__note">Загружаем…</p>}
          {detail && <AnalysisDetailView detail={detail} />}
        </div>
      )}
    </li>
  );
}

function AnalysisList({
  api,
  reloadTick,
  isAdmin,
}: {
  api: AppAuthApi;
  reloadTick: number;
  isAdmin: boolean;
}) {
  const [items, setItems] = useState<AnalysisListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setError(null);
    api.listAnalyses().then(
      (list) => active && setItems(list),
      (err: unknown) =>
        active && setError(err instanceof Error ? err.message : 'Не удалось загрузить проверки'),
    );
    return () => {
      active = false;
    };
  }, [api, reloadTick]);

  useEffect(() => {
    if (!items || !items.some((item) => PENDING_STATUSES.has(item.status))) return;
    const timer = setInterval(() => {
      api.listAnalyses().then(
        (list) => setItems(list),
        () => {},
      );
    }, 4000);
    return () => clearInterval(timer);
  }, [items, api]);

  return (
    <section className="analyses">
      <p className="eyebrow">{isAdmin ? 'Все проверки' : 'Ваши проверки'}</p>
      {error && <p className="analyses__note analyses__note--error">{error}</p>}
      {!error && !items && <p className="analyses__note">Загружаем проверки…</p>}
      {!error && items && items.length === 0 && (
        <p className="analyses__note">Пока пусто — вставьте ссылку на видео выше.</p>
      )}
      {items && items.length > 0 && (
        <ul className={isAdmin ? 'analyses__grid' : 'analyses__list'}>
          {items.map((item) => (
            <AnalysisCard
              key={item.id}
              api={api}
              item={item}
              isAdmin={isAdmin}
              variant={isAdmin ? 'card' : 'row'}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

type AdminLoginFormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string };

function AdminLoginForm({
  api,
  onSuccess,
}: {
  api: AppAuthApi;
  onSuccess: (session: AuthSession) => void;
}) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [state, setState] = useState<AdminLoginFormState>({ status: 'idle' });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state.status === 'submitting' || login.trim().length === 0 || password.length === 0) return;

    setState({ status: 'submitting' });
    try {
      const session = await api.loginWithPassword(login.trim(), password);
      onSuccess(session);
    } catch (error) {
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Не удалось войти',
      });
    }
  }

  return (
    <section className="state-card state-card--center">
      <p className="eyebrow">Панель администратора</p>
      <h1>Вход в STORMVIEW</h1>
      <form className="analysis-form admin-form" onSubmit={submit}>
        <label className="analysis-form__label" htmlFor="admin-login">
          Логин
        </label>
        <input
          id="admin-login"
          className="analysis-form__input"
          type="text"
          autoComplete="username"
          value={login}
          onChange={(event) => setLogin(event.target.value)}
          disabled={state.status === 'submitting'}
        />
        <label className="analysis-form__label" htmlFor="admin-password">
          Пароль
        </label>
        <input
          id="admin-password"
          className="analysis-form__input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={state.status === 'submitting'}
        />
        <button
          className="button"
          type="submit"
          disabled={
            state.status === 'submitting' || login.trim().length === 0 || password.length === 0
          }
        >
          {state.status === 'submitting' ? 'Входим…' : 'Войти'}
        </button>

        {state.status === 'error' && (
          <p className="analysis-form__note analysis-form__note--error" role="alert">
            {state.message}
          </p>
        )}
      </form>
    </section>
  );
}

export function App({ auth, telegram = defaultTelegram }: AppProps) {
  const authApi = useMemo(() => auth ?? createAuthApi(), [auth]);
  const shareToken = useMemo(() => shareTokenFromLocation(), []);
  const [state, setState] = useState<ViewState>({ status: 'loading' });
  const [analysisReloadTick, setAnalysisReloadTick] = useState(0);
  const [showSettings, setShowSettings] = useState(() => isSettingsRoute());

  useEffect(() => {
    // Публичный пересказ читают без входа — авторизацию тут не затеваем вовсе.
    if (shareToken) return;

    let active = true;
    const initData = telegram.initialize();

    async function authenticate() {
      try {
        const restored = await authApi.restore();
        if (!active) return;

        if (restored) {
          setState({ status: 'authenticated', session: restored, loggingOut: false });
          return;
        }

        if (!initData) {
          setState({
            status: isAdminRoute() || isSettingsRoute() ? 'admin-login' : 'outside-telegram',
          });
          return;
        }

        const session = await authApi.login(initData);
        if (active) {
          setState({ status: 'authenticated', session, loggingOut: false });
        }
      } catch (error) {
        if (active) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Не удалось выполнить вход',
          });
        }
      }
    }

    void authenticate();
    return () => {
      active = false;
    };
  }, [authApi, telegram, shareToken]);

  async function logout() {
    if (state.status !== 'authenticated' || state.loggingOut) return;

    setState({ ...state, loggingOut: true });
    try {
      await authApi.logout();
      setState({ status: 'signed-out' });
    } catch (error) {
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Не удалось завершить сессию',
      });
    }
  }

  if (shareToken) {
    return <PublicReportPage api={authApi} token={shareToken} />;
  }

  const adminView =
    state.status === 'authenticated' && state.session.user.role === 'ADMIN';

  return (
    <main className={`shell${adminView ? ' shell--admin' : ''}`}>
      <div className="ambient ambient--one" />
      <div className="ambient ambient--two" />

      <header className="brand">
        <span className="brand__mark" aria-hidden="true">
          S
        </span>
        <span>
          <strong>STORMVIEW</strong>
          <small>fact intelligence</small>
        </span>
      </header>

      {state.status === 'loading' && (
        <section className="state-card state-card--center" role="status">
          <span className="loader" aria-hidden="true" />
          <p>Устанавливаем защищённое соединение…</p>
        </section>
      )}

      {state.status === 'authenticated' && showSettings && adminView && (
        <SettingsPage
          api={authApi}
          onBack={() => {
            setShowSettings(false);
            window.history.pushState({}, '', '/admin');
          }}
        />
      )}

      {state.status === 'authenticated' && !(showSettings && adminView) && (
        <section className="dashboard">
          <div className="profile">
            <UserAvatar user={state.session.user} />
            <div>
              <p className="eyebrow">
                {state.session.user.role === 'ADMIN' ? 'Администратор' : 'Telegram подключён'}
              </p>
              <h1>Здравствуйте, {displayName(state.session.user)}</h1>
            </div>
          </div>

          <article className="hero-card">
            <div className="hero-card__signal" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </div>
            <p className="eyebrow">Контроль достоверности</p>
            <h2>Отделяйте факты от уверенной подачи</h2>
            <p>
              STORMVIEW разберёт видео на проверяемые утверждения, найдёт источники и покажет, чему
              можно доверять.
            </p>
            <AnalysisForm
              api={authApi}
              onQueued={() => setAnalysisReloadTick((tick) => tick + 1)}
            />
          </article>

          {adminView && (
            <button
              className="button button--ghost"
              type="button"
              onClick={() => {
                setShowSettings(true);
                window.history.pushState({}, '', '/admin/settings');
              }}
            >
              ⚙ Настройки слежения
            </button>
          )}

          <AnalysisList
            api={authApi}
            reloadTick={analysisReloadTick}
            isAdmin={state.session.user.role === 'ADMIN'}
          />

          <div className="security-note">
            <span aria-hidden="true">✓</span>
            <p>
              Сессия защищена HttpOnly cookie
              <small>
                {state.session.user.telegramUserId === ADMIN_SENTINEL_TELEGRAM_ID
                  ? 'Вход по паролю'
                  : `Telegram ID: ${state.session.user.telegramUserId}`}
              </small>
            </p>
          </div>

          <button className="button button--ghost" type="button" onClick={() => void logout()}>
            {state.loggingOut ? 'Завершаем…' : 'Выйти'}
          </button>
        </section>
      )}

      {state.status === 'outside-telegram' && (
        <section className="state-card state-card--center">
          <span className="state-card__icon" aria-hidden="true">
            ↗
          </span>
          <p className="eyebrow">Требуется Telegram</p>
          <h1>Откройте STORMVIEW в Telegram</h1>
          <p>Запустите Mini App из профиля или меню бота — вход произойдёт автоматически.</p>
        </section>
      )}

      {state.status === 'admin-login' && (
        <AdminLoginForm
          api={authApi}
          onSuccess={(session) =>
            setState({ status: 'authenticated', session, loggingOut: false })
          }
        />
      )}

      {state.status === 'signed-out' && (
        <section className="state-card state-card--center">
          <span className="state-card__icon" aria-hidden="true">
            ✓
          </span>
          <h1>Сессия завершена</h1>
          <p>Закройте окно или перезапустите приложение из Telegram, чтобы войти снова.</p>
        </section>
      )}

      {state.status === 'error' && (
        <section className="state-card state-card--error" role="alert">
          <p className="eyebrow">Ошибка авторизации</p>
          <h1>Не удалось войти</h1>
          <p>{state.message}</p>
        </section>
      )}

      <footer>Проверяйте важное. Не доверяйте громкому.</footer>
    </main>
  );
}
